import { format } from 'date-fns';
import { SPECIAL_CATEGORIES } from '../../../SearchForm/utils';
import { AviaPassengerType, SearchParametersAirportFragment, SearchParametersFragment } from '@websky/graphql';
import { Config } from '../../../context';
import { Currency } from '../../../enums';
import { PassengerTypeConfig } from '../../../SearchForm/types';

const PROMO_REGEX = 'PROMO([a-zA-Zа-яА-Я0-9]*?)';

const isCurrentCompanySwitchedToAutoGeneration = () => {
	return ['ZZ', 'F7', 'D2', 'SZ', 'Y7', 'IO'].includes(window.__webskyEngineConfig?.global?.companyInfo?.iataCode);
};

export const getIATA = (data: string) => {
	return data.substr(0, 3);
};

export const isDateGuard = (date: Date | string): date is Date => {
	return !!(date as Date).getTime;
};

export const getAbsoluteDate = (isoString: string): string => {
	return isoString.substr(0, 19);
};

export const getDateWithoutTimezone = (date: Date | string) => {
	return dateConverter(date, false);
};

export const getDateWithTimezone = (date: Date | string) => {
	return dateConverter(date, true);
};

export const getAirport = (airport: SearchParametersAirportFragment) => {
	if (airport.city?.name) {
		return { ...airport, name: airport.city.name };
	}

	return airport;
};

const dateConverter = (date: Date | string, withTimezone: boolean) => {
	if (isDateGuard(date)) {
		const dateWithoutTimezone = new Date(
			withTimezone ? date.getTime() : date.getTime() + date.getTimezoneOffset() * 60000
		);
		dateWithoutTimezone.setMilliseconds(0);

		return dateWithoutTimezone;
	} else {
		const newDate = new Date(date);

		const dateWithoutTimezone = new Date(
			withTimezone ? newDate.getTime() : newDate.getTime() + newDate.getTimezoneOffset() * 60000
		);
		dateWithoutTimezone.setMilliseconds(0);

		return dateWithoutTimezone;
	}
};

export const getDate = (data: string) => {
	const year = data.slice(0, 4);
	const month = data.slice(4, 6);
	const day = data.slice(6, 8);

	return getDateWithoutTimezone(`${year}-${month}-${day}`);
};

export const getPassenger = (passengersConfig: PassengerTypeConfig[], data: string) => {
	const type = data.substr(0, 3),
		count = parseInt(data.substr(3, 1)),
		isExtendedPassengerType = SPECIAL_CATEGORIES.includes(type as any);

	const passengerType = isExtendedPassengerType
		? passengersConfig.find(config => config.code === type)?.parentCode || AviaPassengerType.ADT
		: type;

	return {
		count,
		passengerType: passengerType as AviaPassengerType,
		extendedPassengerType: isExtendedPassengerType ? type : null
	};
};

/**
 * Use for smooth transition to regex auto generation
 *
 * @deprecated
 */
const isPassengerNext__deprecated = (data: string) => {
	return /^(ADT|GCF|CLD|INF|INS|МЛА|SCM|SCF|PDA|YTH|SRC)(.)*\d/.test(data);
};

/**
 * @deprecated
 */
const isFastSearchCorrect__deprecated = (fastSearch: string) => {
	return /^(([A-Z]|[А-Я]){6}\d{8})+((((ADT|МЛА|CLD)\d)|((SCF|SCM|YTH|SRC|МЛА)\d)+)((GCF|CLD|INF|INS)\d)*)?((PROMO([a-zA-Zа-яА-Я0-9]+))|(PAYBYMILES))?(RUB|USD|KZT|GEL|EUR|AMD|UZS|AZN|TRY|RSD|ILS|CNY|GBP)?$/.test(
		fastSearch
	);
};

/**
 * @deprecated
 */
const _WZ_isFastSearchCorrect__deprecated = (fastSearch: string) => {
	return /^(([A-Z]|[А-Я]){6}\d{8})+((((ADT|PDA|CLD|INF|INS)\d)+)|((SCF|SCM|МЛА|CLD|INF|INS)\d)+)?(PROMO([a-zA-Zа-яА-Я0-9]+))?(RUB|USD|KZT)?$/.test(
		fastSearch
	);
};

export const isPassengerNext = (passengersConfig: PassengerTypeConfig[], data: string) => {
	const passengerTypes = passengersConfig.map(passenger => passenger.code).join('|');

	return new RegExp(`^(${passengerTypes})(.)*\\d`).test(data);
};

const isPassengerNextChecker = (passengersConfig: PassengerTypeConfig[], str: string) => {
	if (isCurrentCompanySwitchedToAutoGeneration()) {
		return isPassengerNext(passengersConfig, str);
	} else {
		return isPassengerNext__deprecated(str);
	}
};

export const hasPromo = (value: string) => {
	return RegExp(PROMO_REGEX).test(value);
};

export const extractPromo = (value: string) => {
	return value.match(`${PROMO_REGEX}(?=PAYBYMILES|${Object.keys(Currency).join('|')}|$)`)?.[1];
};

export const isPromoOrCurrencyNext = (data: string) => {
	const regex = new RegExp(`^((PROMO([a-zA-Zа-яА-Я0-9]+))|(PAYBYMILES))?(${Object.keys(Currency).join('|')})?$`);

	return regex.test(data);
};

export const extractPromoCodeAndCurrency = (fastSearch: string) => {
	const matchedResult = fastSearch.match(
		new RegExp(`((PROMO([a-zA-Zа-яА-Я0-9]+?))|(PAYBYMILES))?(${Object.keys(Currency).join('|')})?$`)
	);

	return {
		promoCode: matchedResult?.[3] || '',
		payByMiles: matchedResult?.[4] === 'PAYBYMILES',
		currency: matchedResult?.[5] || ''
	};
};

export const fastSearchParse = (
	fastSearch: string,
	passengersConfig: PassengerTypeConfig[],
	allowPromoCodes: boolean
) => {
	let str = fastSearch;

	const segments: SearchParametersFragment['segments'] = [],
		passengers: SearchParametersFragment['passengers'] = [];

	while (!isPassengerNextChecker(passengersConfig, str) && !isPromoOrCurrencyNext(str) && str.length) {
		const departure = getIATA(str),
			arrival = getIATA(str.slice(3)),
			date = getDate(str.slice(6));

		segments.push({
			arrival: {
				iata: arrival
			} as SearchParametersAirportFragment,
			departure: {
				iata: departure
			} as SearchParametersAirportFragment,
			date: format(date, 'yyyy-MM-dd')
		});

		str = str.slice(14);
	}

	while (isPassengerNextChecker(passengersConfig, str)) {
		passengers.push(getPassenger(passengersConfig, str));

		str = str.slice(4);
	}

	if (!passengers.length) {
		passengers.push({
			passengerType: AviaPassengerType.ADT,
			count: 1
		});
	}

	const result: SearchParametersFragment = {
		segments,
		passengers
	};

	const { promoCode, payByMiles, currency } = extractPromoCodeAndCurrency(fastSearch);

	if (allowPromoCodes && promoCode) {
		result.promotionCode = promoCode;
	}

	if (currency) {
		result.currency = currency;
	}

	result.ffpMode = payByMiles;

	return result;
};

export const generateFastSearchRegex = (config: Config): RegExp => {
	const airportsAndDatesRegex = `(([A-Z]|[А-Я]){6}\\d{8})+`;

	const passengerTypes = config.SearchForm.passengersConfig.map(passenger => passenger.code).join('|');
	const passengersRegex = `(((${passengerTypes})\\d)+)?`;

	const promo = config.global.allowPromoCodes ? '(PROMO([a-zA-Zа-яА-Я0-9]+?))' : '';
	const payByMiles = config.SearchForm.payByMiles ? '(PAYBYMILES)' : '';
	const promoAndMilesArr = [promo, payByMiles].filter(Boolean);
	const promoAndMilesRegex = promoAndMilesArr.length ? `(${promoAndMilesArr.join('|')})?` : '';

	const currencyRegex = `(${Object.values(Currency).join('|')})?`;

	return new RegExp(`^${airportsAndDatesRegex}${passengersRegex}${promoAndMilesRegex}${currencyRegex}$`);
};

export const isFastSearchCorrect = (config: Config, fastSearch: string) => {
	if (isCurrentCompanySwitchedToAutoGeneration()) {
		const regex = generateFastSearchRegex(config);

		return regex.test(fastSearch);
	} else if (config.global.companyInfo?.iataCode === 'WZ') {
		return _WZ_isFastSearchCorrect__deprecated(fastSearch);
	} else {
		return isFastSearchCorrect__deprecated(fastSearch);
	}
};
